import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation, useParams } from 'react-router'
import { toJS } from 'mobx'
// evertel
import { PageBody, Text, useUI, ContentPlaceholder, Nav, NavItem, NavLink, TabContent, TabPane, Icon, InfoBox } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SettingsBar } from './elements/SettingsBar'
import { SchemaBuilderController } from '../../controller/schema-builder'
import { SchemaBuilderDnD } from '../schema-builder-dnd'
import { SchemaBuilderPreview } from './SchemaBuilderPreview'
import { SchemaBuilderSettings } from './SchemaBuilderSettings'
import { normalize } from '@evertel/schema-parser'
import { SchemaMediaController } from '../../controller/medias/SchemaMediaController'
import { truncate } from '@evertel/utils'
import { SessionService } from '@evertel/session/SessionService'
import { SchemaProps } from '@evertel/types'


interface SchemaBuilderProps {
    props?: any
}

const SchemaBuilder: React.FC<SchemaBuilderProps> = observer(() => {

    const sessionService = useService(SessionService, [])

    const { toggleLeftNav } = useUI()
    const location = useLocation()
    const { id, target } = useParams()

    const schemaId = Number(id)
    const departmentId = (location.state?.isAdmin || target === 'global') ? 0 : sessionService.selectedDepartmentId

    const schemaBuilderController = useService(SchemaBuilderController, [schemaId, departmentId])
    const schemaMediaController = useService(SchemaMediaController, [])

    const schemaKey = schemaBuilderController.schemaKey

    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState<'full' | 'preview' | 'settings'>('full')

    useEffect(() => {
        (async () => {
            toggleLeftNav('close')

            setIsLoading(true)
            await schemaBuilderController.init(
                schemaId,
                departmentId
            )
            await schemaMediaController.init(
                schemaId
            )
            setIsLoading(false)
        })()
    }, [schemaBuilderController])

    let schema = {} as SchemaProps
    if (schemaBuilderController.schemaKey === 'root') {
        schema = (schemaBuilderController.schema?.layouts) ? normalize(schemaBuilderController.schema) : {} as SchemaProps
    } else {
        schema = normalize(schemaBuilderController.schema.internalSchemas[schemaBuilderController.schemaKey])
    }

    const gotoSchemaTab = (key) => {
        setActiveTab('full')
        schemaBuilderController.setSchemaKey(key)
    }

    const isOrphaned = React.useMemo(() => {
        if (schemaKey === 'root') return false
        const rows = schemaBuilderController.schema?.layouts?.full ?? []
        return !rows.some((r: any) => {
            if (r?.type !== 'row' || !Array.isArray(r.items) || r.items.length !== 1) return false
            const it = r.items[0]
            // legacy variants we’ve seen elsewhere
            return it?.type === `&${schemaKey}` || it?.item?.type === `&${schemaKey}`
        })
    }, [schemaKey])

    // console.log('ID', schemaId)
    // console.log('NORMALIZED', toJS(schema))
    // console.log('RAW', toJS(controller.schema))

    const internalSchemaKeys = (schemaBuilderController.schema?.internalSchemas) ? Object.keys(schemaBuilderController.schema.internalSchemas) : []
    const isSchemaLoaded = !!schemaBuilderController.schema && !!schemaBuilderController.schema.layouts
    return (
        <>
            <SettingsBar
                schemaBuilderController={schemaBuilderController}
            />
            <PageBody>
                <Nav
                    variant="tabs"
                    role="tablist"
                    className="justify-content-center">
                    <NavItem style={{ display: 'flex' }} className="d-flex">
                        <NavLink
                            className="text-nowrap"
                            active={activeTab === 'full'}
                            onClick={() => { gotoSchemaTab('root') }}
                        >
                            <Icon
                                name="table"
                            />
                            <Text
                                className="ms-2 px-2 py-1"
                                color={(schemaBuilderController.schemaKey === 'root') ? 'primary' : ''}
                                bold={schemaBuilderController.schemaKey === 'root'}>
                                Form Builder
                            </Text>
                        </NavLink>
                        {internalSchemaKeys?.map((sKey, idx) => {
                            const row = schemaBuilderController.schema.layouts.full.find((r: Record<string, any>) =>
                                r.type === 'row'
                                && (
                                    (r.items && r.items[0]?.type === 'list' && r.items[0]?.item?.type === `&${sKey}`)
                                    || (r.items && r.items[0]?.type === `&${sKey}`)
                                )
                            )
                            const list = row?.items?.find((i: Record<string, any>) => i.type === `&${sKey}` || i.item?.type === `&${sKey}`)

                            return (
                                <NavLink
                                    key={idx}
                                    active={activeTab === 'full'}
                                    style={{ marginLeft: -10 }}
                                    onClick={() => { gotoSchemaTab(sKey) }}
                                    className="px-2 text-nowrap"
                                >
                                    <Icon
                                        name={(idx === 0) ? 'chevron-right' : 'pipe'}
                                        size={10}
                                    />
                                    <Text
                                        key={idx}
                                        className="px-2 py-1 text-capitalize"
                                        color={(schemaBuilderController.schemaKey === sKey) ? 'primary' : 'body-color'}
                                        bold={schemaBuilderController.schemaKey === sKey}>
                                        {truncate(list?.title || sKey, 15)}
                                    </Text>
                                </NavLink>
                            )
                        })}
                    </NavItem>
                    <NavItem>
                        <NavLink
                            active={activeTab === 'preview'}
                            disabled={!isSchemaLoaded}
                            onClick={() => setActiveTab('preview')}>
                            <Icon
                                name="eye"
                                className="me-2"
                            />
                            <Text>Preview</Text>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            active={activeTab === 'settings'}
                            onClick={() => setActiveTab('settings')}>
                            <Icon
                                name="gear"
                                className="me-2"
                            />
                            <Text>Settings</Text>
                        </NavLink>
                    </NavItem>
                </Nav>
                <TabContent>
                    <TabPane
                        role="tabpanel"
                        visible={activeTab === 'full'}>
                        {(!departmentId) &&
                            <InfoBox color="warning">
                                Global Template
                            </InfoBox>
                        }
                        {isOrphaned && (
                            <div className="p-3">
                                <InfoBox color="warning" className="mb-3">
                                    This subform isn’t referenced in the root layout (orphaned).
                                </InfoBox>
                                <button
                                    className="btn btn-danger"
                                    onClick={() => {schemaBuilderController.removeInternalSchema(schemaKey); gotoSchemaTab('root')} }
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        {(isLoading) ?
                            <ContentPlaceholder />
                            :
                            <SchemaBuilderDnD
                                schemaId={schemaId}
                                schemaBuilderController={schemaBuilderController}
                                schemaMediaController={schemaMediaController}
                            />
                        }
                    </TabPane>
                    <TabPane
                        role="tabpanel"
                        visible={activeTab === 'preview'}>
                        {(schemaBuilderController.isInitialized && activeTab === 'preview') &&
                            <SchemaBuilderPreview
                                controller={schemaBuilderController}
                            />
                        }
                    </TabPane>
                    <TabPane
                        role="tabpanel"
                        visible={activeTab === 'settings'}>
                        {(activeTab === 'settings') &&
                            <SchemaBuilderSettings
                                schemaBuilderController={schemaBuilderController}
                                departmentId={departmentId}
                            />
                        }
                    </TabPane>
                </TabContent>
            </PageBody>
        </>
    )
})

export {
    SchemaBuilder
}
